/**
 * Flow B's catalogue calls — B5, B6 and B7. Every one goes through
 * `../../api/client.js` and nothing else.
 *
 * ── THE CATALOGUE USED TO BE A FILE INSIDE THE BUNDLE ───────────────────────
 * Twelve life domains, their subtitles, three intent groups, ten categories, three
 * providers and their visit fees all lived in `./domains.ts` and shipped to every phone.
 * A provider who raised a visit fee, a category that closed, a domain that went live —
 * none of them could change without a deploy, and the app would have gone on showing
 * `৳ 300` and `নূর রেফ্রিজারেশন` to everybody until one happened.
 *
 * These three routes are docs/05 § offerings, quoted exactly:
 *
 *     GET /v1/domains                    → 12 domains, is_live flag
 *     GET /v1/domains/:id/intents        → verb-shaped groups
 *     GET /v1/categories/:id/offerings   → paginated, filters: nearby|verified|today
 *
 * ── ALL THREE ARE MOUNTED NOW ───────────────────────────────────────────────
 * W2-01 built them: `apps/api/src/catalogue`, mounted at `/v1` and public, answering
 * from real rows. This client was written against the CONTRACT rather than against
 * whatever rows existed at the time, and the shapes below are the contract's
 * (`@oprappo/contracts § catalogue.ts`) — a subset of it, because a field this screen
 * does not draw is a field it does not read.
 *
 * Where a route answers nothing, these screens still render their chrome and no rows —
 * never a bundled fallback. A screen that quietly substitutes design data for live data
 * is a screen that looks correct in review and lies in production, which is the exact
 * defect this change removed from B5, B6 and B7.
 *
 * The visual gate answers all three from `fixtures/api/explore.*.json`, and the chain
 * `fixtures/api/README.md` asks for is now whole: `__tests__/fixtures.test.tsx` pins those
 * files to the design files, and `apps/api/src/catalogue/__tests__/fixtures.test.ts` pins
 * them to the API's own output.
 *
 * ── WHY THE GLYPH IS A KEY AND NOT A PATH ───────────────────────────────────
 * A domain row carries a `glyph` key from a closed set, and the screen maps it to a drawn
 * mark. The same decision `@oprappo/contracts § ACTIVITY_GLYPHS` makes, for the same
 * reason: free text would let the server ask for a mark no frame draws, and an SVG path in
 * a payload would put the design system on the wire where no designer can see it.
 */
import { api } from '../../api/client.js';

/**
 * The marks B5 draws, as a closed set.
 *
 * Eleven keys for twelve domains — `Oprappo Architecture.dc.html § 04`'s domain 10,
 * ডিজিটাল ও যোগাযোগ, has no glyph in ANY design file, so a row may carry `null` and the
 * screen holds the slot open with nothing in it. Inventing a twelfth mark is not a
 * decision this module gets to make; see `./domains.ts § reported gaps`.
 */
export const DOMAIN_GLYPHS = [
  'bill',
  'food',
  'market',
  'transport',
  'serviceDomain',
  'home',
  'pin',
  'education',
  'work',
  'family',
  'government',
] as const;
export type DomainGlyphKey = (typeof DOMAIN_GLYPHS)[number];

export function isDomainGlyphKey(value: string): value is DomainGlyphKey {
  return (DOMAIN_GLYPHS as readonly string[]).includes(value);
}

/** One row of B5. `subtitle_bn` is null on the rows B5 draws without one. */
export interface DomainRow {
  /** The two-digit life-domain code from the architecture table, as a router param. */
  id: string;
  name_bn: string;
  subtitle_bn: string | null;
  is_live: boolean;
  /** Null where no design file draws a mark for this domain. */
  glyph: string | null;
}

/** `GET /v1/domains` — B5. Twelve rows, live and coming, in the server's order. */
export interface DomainsResponse {
  items: DomainRow[];
}

/** One verb-shaped group of B6. Headings are verbs, not trade names. */
export interface IntentGroupRow {
  id: string;
  heading_bn: string;
  categories: { id: string; label_bn: string }[];
}

/** `GET /v1/domains/:id/intents` — B6. */
export interface DomainIntentsResponse {
  id: string;
  name_bn: string;
  groups: IntentGroupRow[];
}

/** One provider card on B7. */
export interface CategoryOffering {
  id: string;
  name_bn: string;
  /** `৪.৭ ★ · ২৩০ কাজ · ৩০ মিনিটে` — one localised line, never composed here. */
  meta_bn: string;
  /**
   * Integer minor units (paisa) — docs/05 § conventions — and NULLABLE, as
   * `@oprappo/contracts § CatalogueOffering` says in words: "Null where there is none —
   * never `৳ 0`, which would be a claim." Most listings have no visit fee at all
   * (grocery, recharge, anything Oprappo runs itself), and the card must draw nothing
   * rather than a free visit nobody offered.
   *
   * The rest of the contract's card — `price_mode`, `price_bn`, `is_available`,
   * `unavailable_reason_bn` — is not read here yet, so D2's unavailable card cannot be
   * drawn. That is W3-01's (`apps/web/src/routes/explore/**`); a missing field renders
   * nothing, which is the honest state, while a wrong type renders a wrong number.
   */
  visit_fee_minor: number | null;
}

/**
 * `GET /v1/categories/:id/offerings` — B7.
 *
 * `count_bn` is `১২ জন · মিরপুর ১০-এর কাছে`: a count and the caller's own area in one
 * Bangla phrase. It is the server's because half of it is a fact about the person asking
 * and the other half is grammar — the same argument `SearchResultGroup.count_bn` makes.
 * `domain_id` travels with it so B7's back arrow can reach B6 without re-reading a route
 * parameter the server has already resolved.
 */
export interface CategoryOfferingsResponse {
  domain_id: string;
  id: string;
  name_bn: string;
  count_bn: string;
  items: CategoryOffering[];
}

export function fetchDomains(signal?: AbortSignal): Promise<DomainsResponse> {
  return api.get<DomainsResponse>('/v1/domains', { signal });
}

export function fetchDomainIntents(
  domainId: string,
  signal?: AbortSignal,
): Promise<DomainIntentsResponse> {
  return api.get<DomainIntentsResponse>(
    `/v1/domains/${encodeURIComponent(domainId)}/intents`,
    { signal },
  );
}

/**
 * B7's providers.
 *
 * The category id is the whole address. `:domain` is in the URL the person is looking at,
 * but a category belongs to exactly one domain and the server knows which — sending both
 * would let a client ask for a category under the wrong domain and make the server decide
 * which half of the request to believe.
 */
export function fetchCategoryOfferings(
  categoryId: string,
  signal?: AbortSignal,
): Promise<CategoryOfferingsResponse> {
  return api.get<CategoryOfferingsResponse>(
    `/v1/categories/${encodeURIComponent(categoryId)}/offerings`,
    { signal },
  );
}
